package dev.lingvo.site.components.editor

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.foundation.layout.Spacer
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.attr
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.lineHeight
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import dev.lingvo.shared.CommentDto
import dev.lingvo.shared.KeyRow
import dev.lingvo.site.api.Async
import dev.lingvo.site.api.Session
import dev.lingvo.site.ds.AsyncFailureState
import dev.lingvo.site.ds.LBadge
import dev.lingvo.site.ds.LAlert
import dev.lingvo.site.ds.LAlertVariant
import dev.lingvo.site.ds.LBadgeVariant
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonSize
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LCard
import dev.lingvo.site.ds.LSkeleton
import dev.lingvo.site.ds.LTextArea
import dev.lingvo.site.ds.LVisuallyHiddenStyle
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.TypeScale
import dev.lingvo.site.i18n.t
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

@Composable
internal fun EditorDiscussContent(state: TranslationEditorState, row: KeyRow) {
    var draft by remember(row.key.id) { mutableStateOf("") }
    val currentUserId = Session.user?.id
    val resolution = state.panels.commentResolution
    Column(Modifier.fillMaxWidth().gap(Space.MD)) {
        resolution.problems[row.key.id]?.let { problem ->
            LAlert(title = problem, variant = LAlertVariant.WARNING, live = true)
            LButton(
                label = t("editor.editor_discuss_content.reload_discussion", "Reload discussion"),
                onClick = { resolution.reload(row.key.id) },
                enabled = !resolution.isBusy(row.key.id),
                loading = resolution.reloading[row.key.id] == true,
                size = LButtonSize.SM,
            )
        }
        when (val comments = state.panels.comments[row.key.id] ?: Async.Loading) {
            Async.Loading -> {
                Span(LVisuallyHiddenStyle.toModifier().toAttrs { attr("role", "status") }) {
                    Text(t("editor.editor_discuss_content.loading_the_discussion", "Loading the discussion"))
                }
                repeat(3) { LSkeleton(height = 76.px, label = null) }
            }
            // Not `ErrorState`: a key a colleague deleted, or a comment thread this reader is no
            // longer allowed to see, is not a fault and must not offer a Retry that will fail the
            // same way for ever. The editor is the surface held open longest, so both are real.
            is Async.Failure -> AsyncFailureState(
                failure = comments,
                subject = t("editor.editor_discuss_content.these_comments", "these comments"),
                onRetry = {
                    state.panels.comments.remove(row.key.id)
                    state.panels.ensureDiscussLoaded(row.key.id)
                },
            )
            is Async.Success -> if (comments.value.isEmpty()) {
                EditorUtilityPanelEmpty(
                    name = "comments",
                    title = t("editor.editor_discuss_content.no_comments", "No comments"),
                    description = if (state.capability.canWriteTranslations) {
                        t("editor.editor_discuss_content.leave_context_for_reviewers_and_future", "Leave context for reviewers and future translators.")
                    } else {
                        t("editor.editor_discuss_content.comments_and_reviewer_context_will_appear", "Comments and reviewer context will appear here.")
                    },
                )
            } else {
                comments.value.forEach { comment ->
                    CommentCard(comment, currentUserId, state.capability.canWriteTranslations,
                        resolution.busy[comment.id] == true,
                        resolution.problems[row.key.id] == null && resolution.reloading[row.key.id] != true,
                        onResolve = { resolution.update(comment) })
                }
            }
        }
        if (state.capability.canWriteTranslations) {
            Column(
                EditorUtilityCommentComposerStyle.toModifier()
                    .attr("data-editor-comment-composer", "true")
                    .attr("data-editor-text-context", "utility"),
            ) {
                LTextArea(
                    value = draft,
                    onValueChange = { draft = it },
                    label = t("editor.editor_discuss_content.add_a_comment", "Add a comment"),
                    placeholder = t("editor.editor_discuss_content.share_translation_context", "Share translation context…"),
                )
                Row(Modifier.fillMaxWidth()) {
                    Spacer()
                    LButton(
                        label = t("editor.editor_discuss_content.post_comment", "Post comment"),
                        onClick = { state.panels.postComment(row.key.id, draft) { draft = "" } },
                        enabled = draft.isNotBlank(),
                        loading = state.panels.postingComments[row.key.id] == true,
                        size = LButtonSize.SM,
                    )
                }
            }
        }
    }
}

@Composable
private fun CommentCard(comment: CommentDto, currentUserId: String?, canWrite: Boolean, busy: Boolean, enabled: Boolean, onResolve: () -> Unit) {
    val bodyId = "lingvo-comment-body-${comment.id}"
    LCard(modifier = Modifier.width(100.percent)) {
        Column(Modifier.gap(Space.SM)) {
            Row(Modifier.fillMaxWidth().gap(Space.SM), verticalAlignment = Alignment.CenterVertically) {
                Span(Modifier.fontSize(TypeScale.Small).fontWeight(700).toAttrs()) {
                    Text(authorLabel(comment.authorId, currentUserId))
                }
                comment.locale?.let { LBadge(it, variant = LBadgeVariant.NEUTRAL) }
                if (comment.resolved) LBadge(t("editor.editor_discuss_content.resolved", "Resolved"), variant = LBadgeVariant.SUCCESS)
                Spacer()
                EditorAssistMetaText(compactTimestamp(comment.createdAt))
            }
            Div(
                Modifier.fontSize(TypeScale.BodySmall).lineHeight(1.55).styleModifier {
                    property("white-space", "pre-wrap")
                    property("overflow-wrap", "anywhere")
                }.attr("id", bodyId).toAttrs(),
            ) { Text(comment.body) }
            if (canWrite) {
                Row(Modifier.fillMaxWidth()) {
                    Spacer()
                    LButton(
                        label = if (comment.resolved) t("editor.editor_discuss_content.reopen", "Reopen") else t("editor.editor_discuss_content.resolve", "Resolve"),
                        onClick = onResolve,
                        modifier = Modifier.attr("aria-describedby", bodyId),
                        variant = LButtonVariant.GHOST,
                        size = LButtonSize.SM,
                        enabled = enabled,
                        loading = busy,
                    )
                }
            }
        }
    }
}
